import { analytics } from '@play-co/gcinstant';
import { now, user } from 'src/StateObserver';
import { wasLoadedFromSavedPayload } from 'src/lib/native/native.util';
import features from 'src/replicant/ruleset/features';
import { buildParameters } from './crossplay.utils';
import type {
  CrossPlayStartParams,
  CrossPlayFinishParams,
} from './crossplay.types';
import { track } from 'src/lib/analytics/utils';

type CrossplayNative = { target: 'native' };
type CrossplayWeb = { target: 'web' };

/**
 * Tracks the start of a open web crossplay session.
 *
 * @param args - The parameters required to start crossplay.
 * @param install - Optional flag indicating whether this is an installation event. Defaults to `false`.
 */
export function trackWebCrossplayStart(
  args: CrossPlayStartParams & CrossplayWeb,
  install = false,
) {
  analytics.game.crossplay.trackCrossplayStart({
    ...buildParameters(args, install),
    eventNameOverride: 'WebCrossplayStart',
  });
}

/**
 * Tracks the completion of a open web crossplay session.
 *
 * @param args - The parameters required to track the crossplay finish event.
 * @param args.timestamp - The timestamp when the crossplay session started.
 *
 * This function sends an analytics event to track the completion of a crossplay session.
 * It calculates the elapsed time since the crossplay session started and includes it in the event data.
 */
export function trackWebCrossplayFinish(
  args: CrossPlayFinishParams & CrossplayWeb,
) {
  analytics.game.crossplay.trackCrossplayFinish({
    ...buildParameters(args, wasLoadedFromSavedPayload()),
    elapsedSecondsCrossplayStart: args.timestamp
      ? Math.floor((now() - args.timestamp) / 1000)
      : undefined,
    eventNameOverride: 'WebCrossplayFinish',
  });
}

/**
 * Tracks the start of a native crossplay session.
 *
 * @param args - The parameters required to start crossplay.
 * @param install - Optional flag indicating whether this is an installation event. Defaults to `false`.
 */
export function trackNativeCrossplayStart(
  args: CrossPlayStartParams & CrossplayNative,
  install = false,
) {
  analytics.game.crossplay.trackCrossplayStart(buildParameters(args, install));
}

/**
 * Tracks the completion of a native crossplay session.
 *
 * @param args - The parameters required to track the crossplay finish event.
 * @param args.timestamp - The timestamp when the crossplay session started.
 *
 * This function sends an analytics event to track the completion of a crossplay session.
 * It calculates the elapsed time since the crossplay session started and includes it in the event data.
 */
export function trackNativeCrossplayFinish(
  args: CrossPlayFinishParams & CrossplayNative,
) {
  analytics.game.crossplay.trackCrossplayFinish({
    ...buildParameters(args, wasLoadedFromSavedPayload()),
    elapsedSecondsCrossplayStart: args.timestamp
      ? Math.floor((now() - args.timestamp) / 1000)
      : undefined,
  });
}

/**
 * Tracks the event of saving the session state.
 *
 * @param args - The parameters required to start a cross-play session.
 */
export function trackSessionStateSave(args: CrossPlayStartParams) {
  if (features(user()).amplitudeEventThrottle) return;

  track('SessionStateSave', buildParameters(args, wasLoadedFromSavedPayload()));
}

/**
 * Tracks the session state load event.
 *
 * This function calculates the elapsed time since the last crossplay switch and sends an analytics event
 * with the session state load details.
 *
 * @param args - The parameters required to start the cross-play session.
 * @param args.timestamp - The timestamp of the last switch.
 *
 * @returns void
 */
export function trackSessionStateLoad(args: CrossPlayStartParams) {
  const lastSwitch = args.timestamp;
  const elapsedSecondsSaveState = lastSwitch
    ? Math.floor((now() - lastSwitch) / 1000)
    : undefined;

  track('SessionStateLoad', {
    ...buildParameters(args, wasLoadedFromSavedPayload()),
    elapsedSecondsSaveState,
  });
}
